import React, { useEffect, useState } from "react";
import {
    Target,
    BarChart3,
    ShieldAlert,
    Users,
    Zap,
    Calendar,
    Check,
    Lock,
    ChevronRight,
    Info,
    Wallet,
    FileText,
    ShieldCheck,
    Instagram
} from "lucide-react";

// CONNECTED DIRECTLY TO YOUR PARLAYGG GOOGLE SHEET
const GOOGLE_SHEET_CSV_URL =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vTYjhGZOkh_8zCBGwtZnQmmgxsZu7qdY5Hk3dn06Kchppe6iMEze3zD226gi6tXv8zeao4ZKOCBm-JK/pub?output=csv";

// WHOP CHECKOUT LINK
const WHOP_LINK =
    "https://whop.com/joined/parlaygg/products/parlay-of-the-day-bc/";

export default function LandingPage() {
    const [activePlay, setActivePlay] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasAcceptedNotice, setHasAcceptedNotice] = useState(false);
    const [stats, setStats] = useState({
        winRate: 0,
        totalBets: 0,
    });

    useEffect(() => {
        const parseCSVLine = (line) => {
            const columns = [];
            let current = "";
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                const nextChar = line[i + 1];

                if (char === '"' && nextChar === '"') {
                    current += '"';
                    i++;
                } else if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === "," && !inQuotes) {
                    columns.push(current.trim());
                    current = "";
                } else {
                    current += char;
                }
            }

            columns.push(current.trim());
            return columns;
        };

        fetch(GOOGLE_SHEET_CSV_URL)
            .then((res) => res.text())
            .then((csvText) => {
                const lines = csvText.replace(/\r/g, "").split("\n").filter(Boolean);

                if (lines.length < 2) {
                    setLoading(false);
                    return;
                }

                const headers = parseCSVLine(lines[0]).map((h) =>
                    h.trim().toLowerCase()
                );

                const dateIdx = headers.indexOf("date");
                const oddsIdx = headers.indexOf("odds");
                const resultIdx = headers.indexOf("result");

                const pastSlips = [];
                const activeSlips = [];
                let winsCount = 0;

                for (let i = 1; i < lines.length; i++) {
                    const columns = parseCSVLine(lines[i]);

                    const rawDate = columns[dateIdx] || "N/A";
                    const rawResult = (columns[resultIdx] || "").trim();
                    const normalizedResult = rawResult.toLowerCase();

                    const processedLegs = [];

                    for (let legNum = 1; legNum <= 5; legNum++) {
                        const teamHeaderIdx = headers.indexOf(`leg ${legNum}`);
                        const resultHeaderIdx = headers.indexOf(`leg ${legNum} result`);

                        const teamValue =
                            teamHeaderIdx !== -1
                                ? (columns[teamHeaderIdx] || "").trim()
                                : "";

                        const legResultValue =
                            resultHeaderIdx !== -1
                                ? (columns[resultHeaderIdx] || "").trim()
                                : "";

                        if (teamValue !== "") {
                            processedLegs.push({
                                teamName: teamValue,
                                isLegWin: legResultValue.toLowerCase() === "won",
                            });
                        }
                    }

                    const slipData = {
                        date: rawDate,
                        timestamp: Date.parse(rawDate) || 0,
                        rowNumber: i,
                        legs: processedLegs,
                        odds: columns[oddsIdx] || "N/A",
                        result: rawResult.toUpperCase(),
                    };

                    // BLANK RESULT = NEW ACTIVE PARLAY
                    if (rawResult === "") {
                        activeSlips.push(slipData);
                        continue;
                    }

                    // RESULT NOT BLANK = PAST HISTORY ONLY
                    const isParlayWin = normalizedResult === "won";

                    if (isParlayWin) winsCount++;

                    pastSlips.push({
                        ...slipData,
                        isWin: isParlayWin,
                    });
                }

                pastSlips.sort((a, b) => {
                    if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
                    return b.rowNumber - a.rowNumber;
                });

                activeSlips.sort((a, b) => {
                    if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
                    return b.rowNumber - a.rowNumber;
                });

                const total = pastSlips.length;
                const rate = total > 0 ? Math.round((winsCount / total) * 100) : 0;

                setStats({
                    winRate: rate,
                    totalBets: total,
                });

                setActivePlay(activeSlips[0] || null);
                setHasAcceptedNotice(false);
                setHistory(pastSlips);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Data tracking connection failed:", err);
                setLoading(false);
            });
    }, []);

    const handleBuyClick = () => {
        if (!hasAcceptedNotice) return;
        window.open(WHOP_LINK, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="page">
            <style>
                {`
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
          }

          .page {
            min-height: 100vh;
            background: #070906;
            color: #fff;
            font-family: Arial, Helvetica, sans-serif;
            overflow-x: hidden;
          }

          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
          }

          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.03); }
            100% { transform: scale(1); }
          }

          .blink {
            animation: blink 1s infinite;
          }

          .pulse-btn {
            animation: pulse 1.7s infinite;
          }

          .hero {
            padding: 4rem 5% 2rem;
            text-align: center;
          }

          .hero-inner {
            max-width: 800px;
            margin: 0 auto;
          }

          .hero-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            background: #11160f;
            border: 1px solid #1b2318;
            padding: 0.5rem 1rem;
            border-radius: 999px;
            margin-bottom: 1.25rem;
          }

          .hero-badge span {
            color: #a3ff12;
            font-size: 0.8rem;
            font-weight: 700;
            letter-spacing: 0.05em;
          }

          .hero-title {
            font-size: clamp(2.15rem, 8vw, 3.5rem);
            font-weight: 900;
            margin: 0 0 1rem;
            line-height: 1.08;
            letter-spacing: -0.04em;
          }

          .hero-title span {
            color: #a3ff12;
            text-shadow: 0 0 20px rgba(163, 255, 18, 0.2);
          }

          .hero-subtitle {
            color: #8e9a88;
            font-size: clamp(0.95rem, 3vw, 1.1rem);
            line-height: 1.55;
            max-width: 580px;
            margin: 0 auto;
          }

          .active-section {
            padding: 1rem 5% 4rem;
            display: flex;
            justify-content: center;
          }

          .active-wrap {
            width: 100%;
            max-width: 540px;
          }

          .loading-box {
            text-align: center;
            padding: 3rem 1rem;
            color: #8e9a88;
            font-weight: 700;
          }

          .active-card {
            background: #0a0f08;
            border: 3px solid #a3ff12;
            border-radius: 20px;
            padding: clamp(1.5rem, 5vw, 2.5rem) clamp(1rem, 5vw, 2rem);
            position: relative;
            overflow: hidden;
            box-shadow: 0 0 50px rgba(163, 255, 18, 0.15);
          }

          .live-badge {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            justify-content: center;
            background: rgba(163, 255, 18, 0.12);
            border: 1px solid #a3ff12;
            width: fit-content;
            margin: 0 auto 1.5rem;
            padding: 0.45rem 1.25rem;
            border-radius: 6px;
          }

          .live-dot {
            width: 8px;
            height: 8px;
            background: #a3ff12;
            border-radius: 50%;
            flex: 0 0 auto;
          }

          .live-badge span {
            font-size: 0.8rem;
            font-weight: 900;
            color: #a3ff12;
            letter-spacing: 0.06em;
            white-space: nowrap;
          }

          .active-title {
            text-align: center;
            font-size: clamp(1.25rem, 5vw, 1.5rem);
            font-weight: 900;
            margin: 0 0 1.5rem;
            color: #fff;
            line-height: 1.25;
            letter-spacing: -0.02em;
          }

          .locked-preview {
            position: relative;
            min-height: 470px;
            display: flex;
            align-items: stretch;
            justify-content: center;
          }

          .blurred-content {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 0.85rem;
            filter: blur(8px);
            pointer-events: none;
            user-select: none;
            opacity: 0.2;
          }

          .locked-leg {
            display: flex;
            align-items: center;
            background: #11160f;
            border: 1px solid #1b2318;
            padding: 0.9rem 1.25rem;
            border-radius: 8px;
            min-height: 52px;
          }

          .locked-leg-label {
            font-weight: 900;
            color: #a3ff12;
            margin-right: 1rem;
            font-size: 0.8rem;
            white-space: nowrap;
          }

          .locked-leg-name {
            font-weight: 700;
          }

          .locked-stats {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-top: 0.5rem;
          }

          .locked-stat-box {
            background: #11160f;
            border: 1px solid #1b2318;
            padding: 0.75rem;
            border-radius: 8px;
            text-align: center;
          }

          .locked-stat-box p:first-child {
            font-size: 0.65rem;
            color: #8e9a88;
            margin: 0 0 0.15rem;
            font-weight: 800;
          }

          .locked-stat-box p:last-child {
            font-size: 1.25rem;
            font-weight: 900;
            color: #a3ff12;
            margin: 0;
          }

          .locked-stat-box.ev p:last-child {
            color: #fff;
          }

          .locked-overlay {
            position: absolute;
            inset: 0;
            padding: 1rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            z-index: 2;
          }

          .lock-circle {
            background: #a3ff12;
            color: #080b07;
            padding: 1.1rem;
            border-radius: 50%;
            margin-bottom: 1.1rem;
            box-shadow: 0 0 25px rgba(163, 255, 18, 0.4);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .locked-overlay h3 {
            font-size: clamp(1.15rem, 5vw, 1.4rem);
            font-weight: 900;
            margin: 0 0 0.5rem;
            color: #fff;
            line-height: 1.2;
          }

          .locked-overlay p {
            color: #8e9a88;
            font-size: 0.85rem;
            margin: 0 0 1.1rem;
            max-width: 330px;
            line-height: 1.45;
          }

          .agreement-box {
            width: 100%;
            max-width: 390px;
            margin: 0.25rem 0 1rem;
            background: rgba(17, 22, 15, 0.95);
            border: 1px solid #273121;
            border-radius: 10px;
            padding: 0.85rem;
            text-align: left;
          }

          .agreement-label {
            display: flex;
            align-items: flex-start;
            gap: 0.7rem;
            cursor: pointer;
          }

          .agreement-label input {
            width: 18px;
            height: 18px;
            accent-color: #a3ff12;
            margin-top: 0.1rem;
            flex: 0 0 auto;
            cursor: pointer;
          }

          .agreement-label span {
            color: #cfd8ca;
            font-size: 0.76rem;
            line-height: 1.45;
            font-weight: 600;
          }

          .buy-btn {
            background: #a3ff12;
            color: #080b07;
            border: none;
            width: 100%;
            max-width: 320px;
            padding: 1.1rem;
            border-radius: 8px;
            font-weight: 900;
            font-size: 1.05rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            box-shadow: 0 6px 25px rgba(163, 255, 18, 0.45);
            transition: opacity 0.2s ease, transform 0.2s ease;
          }

          .buy-btn.disabled {
            opacity: 0.45;
            cursor: not-allowed;
            box-shadow: none;
            animation: none;
          }

          .checkbox-helper {
            margin: 0.75rem 0 0 !important;
            max-width: 360px !important;
            font-size: 0.72rem !important;
            line-height: 1.45 !important;
            color: #6f7a69 !important;
          }

          .empty-active {
            background: #11160f;
            border: 1px dashed #1b2318;
            padding: 3rem 2rem;
            border-radius: 16px;
            text-align: center;
            color: #8e9a88;
          }

          .empty-active h3 {
            font-size: 1.25rem;
            font-weight: 900;
            color: #fff;
            margin: 0 0 0.4rem;
          }

          .empty-active p {
            font-size: 0.9rem;
            max-width: 360px;
            margin: 0 auto;
            line-height: 1.45;
          }

          .history-section {
            padding: 3.5rem 0 4rem;
            background: #0a0f08;
            border-top: 1px solid #1b2318;
          }

          .history-inner {
            padding: 0 5%;
            max-width: 1100px;
            margin: 0 auto;
          }

          .history-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-bottom: 1.5rem;
            flex-wrap: wrap;
            gap: 1rem;
          }

          .history-header h2 {
            font-size: clamp(1.25rem, 5vw, 1.5rem);
            font-weight: 900;
            color: #fff;
            margin: 0;
          }

          .history-header h2 span {
            color: #a3ff12;
          }

          .history-header p {
            color: #8e9a88;
            font-size: 0.85rem;
            margin: 0.25rem 0 0;
            line-height: 1.4;
          }

          .stats-box {
            display: flex;
            gap: 1.25rem;
            background: #11160f;
            border: 1px solid #1b2318;
            padding: 0.5rem 1.25rem;
            border-radius: 6px;
            flex-wrap: wrap;
          }

          .stats-box span {
            font-size: 0.8rem;
            color: #8e9a88;
            font-weight: 800;
          }

          .green {
            color: #a3ff12 !important;
          }

          .white {
            color: #fff !important;
          }

          .history-scroll {
            display: flex;
            gap: 1.5rem;
            overflow-x: auto;
            padding: 0.5rem 0 1.5rem;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
          }

          .history-scroll::-webkit-scrollbar {
            height: 8px;
          }

          .history-scroll::-webkit-scrollbar-track {
            background: #0a0f08;
          }

          .history-scroll::-webkit-scrollbar-thumb {
            background: #1b2318;
            border-radius: 99px;
          }

          .history-scroll::-webkit-scrollbar-thumb:hover {
            background: #a3ff12;
          }

          .history-card {
            flex: 0 0 310px;
            scroll-snap-align: start;
            background: #0e130c;
            border-radius: 10px;
            padding: 1.25rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .date-pill {
            display: flex;
            align-items: center;
            gap: 0.3rem;
            align-self: center;
            background: #111610;
            padding: 0.2rem 0.6rem;
            border-radius: 4px;
            border: 1px solid #1e291b;
          }

          .date-pill span {
            font-size: 0.7rem;
            font-weight: 800;
            color: #fff;
          }

          .history-legs {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
          }

          .history-leg {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
            background: #11160f;
            padding: 0.5rem 0.75rem;
            border-radius: 4px;
            border: 1px solid #151c13;
          }

          .history-leg span:first-child {
            font-size: 0.8rem;
            font-weight: 700;
            color: #dcdcdc;
            line-height: 1.25;
          }

          .loss-x {
            color: #ff4a4a;
            font-size: 0.65rem;
            font-weight: 900;
          }

          .history-bottom {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
            margin-top: auto;
          }

          .history-mini-box {
            background: #11160f;
            padding: 0.4rem;
            border-radius: 4px;
            text-align: center;
            border: 1px solid #1b2318;
          }

          .history-mini-box span:first-child {
            color: #8e9a88;
            display: block;
            font-size: 0.6rem;
            font-weight: 800;
          }

          .history-mini-box span:last-child {
            font-size: 1rem;
            font-weight: 900;
          }

          .empty-history {
            background: #11160f;
            border: 1px dashed #1b2318;
            padding: 2rem;
            border-radius: 12px;
            color: #8e9a88;
            width: 100%;
            text-align: center;
          }

          .value-section,
          .education-section,
          .policies-section {
            padding: 4rem 5%;
            background: #0b0f09;
          }

          .education-section {
            background: #070906;
            border-top: 1px solid #1b2318;
          }

          .policies-section {
            background: #0a0f08;
            border-top: 1px solid #1b2318;
          }

          .section-title {
            text-align: center;
            margin: 0 0 1rem;
            font-size: clamp(1.35rem, 5vw, 1.75rem);
            font-weight: 900;
          }

          .section-title span {
            color: #a3ff12;
          }

          .section-subtitle {
            color: #8e9a88;
            font-size: 0.95rem;
            line-height: 1.55;
            max-width: 760px;
            margin: 0 auto 3rem;
            text-align: center;
          }

          .value-grid,
          .education-grid,
          .policies-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 2rem;
            max-width: 1200px;
            margin: 0 auto;
          }

          .value-card,
          .education-card,
          .policy-card {
            background: #11160f;
            border: 1px solid #1b2318;
            padding: 1.75rem;
            border-radius: 8px;
          }

          .value-card h3,
          .education-card h3,
          .policy-card h3 {
            margin: 0 0 0.4rem;
            font-size: 1.15rem;
            font-weight: 800;
          }

          .value-card p,
          .education-card p,
          .policy-card p {
            color: #8e9a88;
            font-size: 0.9rem;
            line-height: 1.5;
            margin: 0;
          }

          .policy-card strong,
          .education-card strong {
            color: #fff;
          }

          .policy-link {
            display: inline-block;
            margin-top: 0.75rem;
            color: #a3ff12;
            font-weight: 800;
            font-size: 0.85rem;
            text-decoration: none;
          }

          .policy-link:hover {
            text-decoration: underline;
          }

          .footer {
            padding: 2.5rem 5%;
            border-top: 1px solid #1b2318;
            text-align: center;
            color: #8e9a88;
            font-size: 0.85rem;
          }

          .footer p {
            margin: 0 0 0.75rem;
            line-height: 1.5;
          }

          .footer-markets {
            display: flex;
            gap: 0.9rem;
            justify-content: center;
            flex-wrap: wrap;
            color: #fff;
            font-weight: 700;
          }

          @media (max-width: 640px) {
            .hero {
              padding: 3rem 1rem 1.5rem;
            }

            .hero-badge {
              padding: 0.45rem 0.8rem;
            }

            .hero-badge span {
              font-size: 0.7rem;
            }

            .active-section {
              padding: 1rem 1rem 3rem;
            }

            .active-card {
              border-width: 2px;
              border-radius: 18px;
            }

            .live-badge {
              width: 100%;
              max-width: 290px;
              padding: 0.45rem 0.75rem;
            }

            .live-badge span {
              font-size: 0.72rem;
            }

            .locked-preview {
              min-height: 540px;
            }

            .locked-leg {
              padding: 0.8rem 0.85rem;
            }

            .locked-leg-label {
              font-size: 0.72rem;
              margin-right: 0.65rem;
            }

            .locked-leg-name {
              font-size: 0.85rem;
            }

            .locked-stats {
              grid-template-columns: 1fr;
              gap: 0.75rem;
            }

            .locked-overlay {
              padding: 0.75rem;
            }

            .lock-circle {
              padding: 0.95rem;
              margin-bottom: 1rem;
            }

            .locked-overlay p {
              font-size: 0.8rem;
              margin-bottom: 1rem;
            }

            .agreement-label span {
              font-size: 0.7rem;
            }

            .checkbox-helper {
              font-size: 0.68rem !important;
            }

            .buy-btn {
              max-width: 100%;
              padding: 1rem;
              font-size: 0.95rem;
            }

            .history-section {
              padding: 3rem 0;
            }

            .history-inner {
              padding: 0 1rem;
            }

            .history-header {
              align-items: flex-start;
            }

            .stats-box {
              width: 100%;
              justify-content: space-between;
              gap: 0.75rem;
            }

            .history-scroll {
              gap: 1rem;
              padding-bottom: 1.25rem;
            }

            .history-card {
              flex-basis: 86vw;
              max-width: 340px;
            }

            .value-section,
            .education-section,
            .policies-section {
              padding: 3rem 1rem;
            }

            .value-grid,
            .education-grid,
            .policies-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }

            .value-card,
            .education-card,
            .policy-card {
              padding: 1.4rem;
            }

            .footer {
              padding: 2rem 1rem;
            }
          }

          @media (max-width: 380px) {
            .hero-title {
              font-size: 2rem;
            }

            .active-title {
              font-size: 1.1rem;
            }

            .locked-preview {
              min-height: 585px;
            }

            .stats-box {
              flex-direction: column;
            }
          }
        `}
            </style>

            {/* HERO SECTION */}
            <header className="hero">
                <div className="hero-inner">
                    <div className="hero-badge">
                        <Zap size={14} color="#A3FF12" />
                        <span>SHARPER PICKS. SMARTER BETS.</span>
                    </div>

                    <h1 className="hero-title">
                        BET WITH <span>MATHEMATICAL EDGE</span>
                    </h1>

                    <p className="hero-subtitle">
                        High-probability research covering esports and major sports
                        markets. Built for disciplined bettors who understand risk.
                    </p>
                </div>
            </header>

            {/* ACTIVE PLAY HUB */}
            <section className="active-section">
                <div className="active-wrap">
                    {loading ? (
                        <div className="loading-box blink">
                            FETCHING LIVE SPORTSBOOK CARD ENGINE...
                        </div>
                    ) : activePlay ? (
                        <div className="active-card">
                            <div className="live-badge">
                                <span className="live-dot blink" />
                                <span>NEW PARLAY NOW AVAILABLE</span>
                            </div>

                            <h2 className="active-title">
                                {activePlay.date.toUpperCase()} MODEL SELECTION
                            </h2>

                            <div className="locked-preview">
                                <div className="blurred-content">
                                    {activePlay.legs.length > 0
                                        ? activePlay.legs.map((leg, index) => (
                                            <div className="locked-leg" key={index}>
                                                <span className="locked-leg-label">
                                                    LEG {index + 1}
                                                </span>
                                                <span className="locked-leg-name">
                                                    {leg.teamName}
                                                </span>
                                            </div>
                                        ))
                                        : [1, 2, 3].map((n) => (
                                            <div className="locked-leg" key={n}>
                                                <span className="locked-leg-label">LEG {n}</span>
                                                <span className="locked-leg-name">
                                                    PRO MODEL LOCK
                                                </span>
                                            </div>
                                        ))}

                                    <div className="locked-stats">
                                        <div className="locked-stat-box">
                                            <p>COMBINED ODDS</p>
                                            <p>{activePlay.odds}</p>
                                        </div>

                                        <div className="locked-stat-box ev">
                                            <p>MARKET TYPE</p>
                                            <p>SPORTS / ESPORTS</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="locked-overlay">
                                    <div className="lock-circle">
                                        <Lock size={26} strokeWidth={2.5} />
                                    </div>

                                    <h3>NEW PARLAY AVAILABLE</h3>

                                    <p>
                                        A new parlay is currently live. It may include esports
                                        and/or sports legs. Unlock the full slip before the games
                                        start.
                                    </p>

                                    <div className="agreement-box">
                                        <label className="agreement-label">
                                            <input
                                                type="checkbox"
                                                checked={hasAcceptedNotice}
                                                onChange={(e) =>
                                                    setHasAcceptedNotice(e.target.checked)
                                                }
                                            />
                                            <span>
                                                This parlay may include esports and/or sports legs.
                                                Please make sure every selection is available to bet
                                                legally in your location before purchasing. Bet
                                                responsibly.
                                            </span>
                                        </label>
                                    </div>

                                    <button
                                        onClick={handleBuyClick}
                                        disabled={!hasAcceptedNotice}
                                        className={`buy-btn ${hasAcceptedNotice ? "pulse-btn" : "disabled"
                                            }`}
                                    >
                                        UNLOCK ON WHOP
                                        <ChevronRight size={18} strokeWidth={2.5} />
                                    </button>

                                    {!hasAcceptedNotice && (
                                        <p className="checkbox-helper">
                                            Please check the box above before continuing to WHOP.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="empty-active">
                            <h3>NO PARLAY CURRENTLY LIVE</h3>
                            <p>
                                The board is currently clear. No active parlay is available at
                                the moment. Check back soon.
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* PAST HISTORY */}
            <section className="history-section">
                <div className="history-inner">
                    <div className="history-header">
                        <div>
                            <h2>
                                VERIFIED <span>HISTORY LOGS</span>
                            </h2>
                        </div>

                        {!loading && history.length > 0 && (
                            <div className="stats-box">
                                <span>
                                    WIN RATE: <span className="green">{stats.winRate}%</span>
                                </span>
                                <span>
                                    TOTAL LOGGED: <span className="white">{stats.totalBets}</span>
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="history-scroll">
                        {!loading && history.length === 0 && (
                            <div className="empty-history">No graded parlay history yet.</div>
                        )}

                        {!loading &&
                            history.map((slip, idx) => (
                                <div
                                    className="history-card"
                                    key={idx}
                                    style={{
                                        border: slip.isWin
                                            ? "1px solid #A3FF12"
                                            : "1px solid #1B2318",
                                    }}
                                >
                                    <div className="date-pill">
                                        <Calendar size={11} color="#A3FF12" />
                                        <span>{slip.date.toUpperCase()}</span>
                                    </div>

                                    <div className="history-legs">
                                        {slip.legs.map((leg, legIdx) => (
                                            <div className="history-leg" key={legIdx}>
                                                <span>{leg.teamName}</span>

                                                {leg.isLegWin ? (
                                                    <Check size={11} color="#A3FF12" strokeWidth={3} />
                                                ) : (
                                                    <span className="loss-x">✕</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="history-bottom">
                                        <div className="history-mini-box">
                                            <span>ODDS</span>
                                            <span className="green">{slip.odds}</span>
                                        </div>

                                        <div
                                            className="history-mini-box"
                                            style={{
                                                border: slip.isWin
                                                    ? "1px solid #A3FF12"
                                                    : "1px solid rgba(255,74,74,0.3)",
                                            }}
                                        >
                                            <span>RESULT</span>
                                            <span
                                                style={{
                                                    color: slip.isWin ? "#A3FF12" : "#ff4a4a",
                                                }}
                                            >
                                                {slip.result}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </section>

            {/* VALUE PROPOSITIONS */}
            <section className="value-section">
                <h2 className="section-title">
                    WHY TRUST OUR <span>PROJECTIONS?</span>
                </h2>

                <p className="section-subtitle">
                    ParlayGG is built around transparency, tracking, and disciplined
                    decision-making. No pick is a guarantee, and every bettor should
                    understand the risk before purchasing or placing a wager.
                </p>

                <div className="value-grid">
                    <div className="value-card">
                        <Target
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Researched Picks</h3>
                        <p>
                            Every play is reviewed through market movement, team form, matchup
                            data, player availability, and pricing value.
                        </p>
                    </div>

                    <div className="value-card">
                        <BarChart3
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Sports & Esports Legs</h3>
                        <p>
                            Parlays may include esports and/or traditional sports depending
                            on where the best slate value is found that day.
                        </p>
                    </div>

                    <div className="value-card">
                        <ShieldAlert
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Location Awareness</h3>
                        <p>
                            Customers should confirm that all legs are available and legal to
                            bet in their own location before purchasing or placing any wager.
                        </p>
                    </div>

                    <div className="value-card">
                        <Users
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Transparent Tracker</h3>
                        <p>
                            The site automatically displays graded results from the tracking
                            sheet, keeping the history clean and verified.
                        </p>
                    </div>
                </div>
            </section>

            {/* BANKROLL EDUCATION */}
            <section className="education-section">
                <h2 className="section-title">
                    BANKROLL <span>MANAGEMENT</span>
                </h2>

                <p className="section-subtitle">
                    Betting should be treated as high-risk entertainment, not guaranteed
                    income. Discipline matters more than one single parlay.
                </p>

                <div className="education-grid">
                    <div className="education-card">
                        <Wallet
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Use Small Units</h3>
                        <p>
                            A common disciplined approach is risking only{" "}
                            <strong>1% to 5% of your bankroll per bet</strong>. Never risk
                            money needed for bills, rent, food, savings, or emergencies.
                        </p>
                    </div>

                    <div className="education-card">
                        <ShieldCheck
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Set Limits First</h3>
                        <p>
                            Decide your budget before betting. Stop when you hit your limit,
                            and do not chase losses after a bad day or bad streak.
                        </p>
                    </div>

                    <div className="education-card">
                        <Info
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>No Guarantees</h3>
                        <p>
                            Even strong research can lose. Parlays are volatile, and every
                            pick should be viewed as an opinion with financial risk.
                        </p>
                    </div>
                </div>
            </section>

            {/* POLICIES */}
            <section className="policies-section">
                <h2 className="section-title">
                    DISCLAIMERS & <span>POLICIES</span>
                </h2>

                <p className="section-subtitle">
                    Please read these terms before purchasing. By buying through WHOP, you
                    understand that ParlayGG provides informational and entertainment
                    opinions only.
                </p>

                <div className="policies-grid">
                    <div className="policy-card">
                        <ShieldAlert
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Responsible Gaming Disclaimer</h3>
                        <p>
                            Gambling involves financial risk. ParlayGG does not guarantee
                            wins, profits, or sportsbook availability. Our content is for
                            informational and entertainment purposes only. Customers are
                            responsible for checking local laws and betting responsibly.
                        </p>
                        <a
                            className="policy-link"
                            href="https://www.ncpgambling.org/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Responsible gaming resources
                        </a>
                    </div>

                    <div className="policy-card">
                        <FileText
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Refund Policy</h3>
                        <p>
                            Because digital picks are delivered instantly or near-instantly,
                            all sales are generally final. No refunds are offered once access
                            to the pick, slip, or private content has been granted, except
                            where required by law or platform policy.
                        </p>
                    </div>

                    <div className="policy-card">
                        <Lock
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Privacy Policy</h3>
                        <p>
                            ParlayGG only collects the information needed to process access,
                            communicate with customers, and manage purchases through platforms
                            like WHOP. We do not sell personal data. Payment and account data
                            may also be handled by WHOP under its own privacy and security
                            policies.
                        </p>
                    </div>

                    <div className="policy-card">
                        <Instagram
                            color="#A3FF12"
                            size={28}
                            style={{ marginBottom: "0.75rem" }}
                        />
                        <h3>Support & Questions</h3>
                        <p>
                            Need help with access, purchases, or have questions about the parlay?
                            Message us on Instagram at <strong>@parlay.gg</strong>. We will do our
                            best to respond as soon as possible.
                        </p>

                        <a
                            className="policy-link"
                            href="https://www.instagram.com/parlay.gg/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Contact @parlay.gg on Instagram
                        </a>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="footer">
                <p>
                    Parlays may include esports and/or sports legs. Always confirm
                    sportsbook availability and legality in your location.
                </p>

                <p>
                    If gambling is causing stress or harm, stop betting and seek help.
                    National Problem Gambling Helpline: 1-800-MY-RESET.
                </p>

                <div className="footer-markets">
                    <span>ESPORTS</span>
                    <span>•</span>
                    <span>MLB</span>
                    <span>•</span>
                    <span>NBA</span>
                    <span>•</span>
                    <span>NHL</span>
                    <span>•</span>
                    <span>NFL</span>
                </div>
            </footer>
        </div>
    );
}